import { Helmet } from "react-helmet";
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "../../../components/layouts/DashboardLayout";
import { Header } from "../../../components/Header";
import { BtnDefault } from "../../../components/buttons/BtnDefault";
import { FaEdit, FaInfoCircle, FaArrowLeft,
    FaBoxOpen,
    FaTag,
    FaPercent,
    
    FaCalendarAlt,
    FaCheckCircle, } from "react-icons/fa";
import { FaTrashCan, FaListCheck, } from "react-icons/fa6";

/* ============================================
   HELPER: Format Rupiah & Tanggal
   ============================================ */
const formatRupiah = (number) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(number);
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
  return new Date(dateString).toLocaleDateString('id-ID', options);
};

/* ============================================
   DATA DUMMY (Simulasi Data dari API)
   ============================================ */
const dummyPackageDetail = {
  id: "pkg-002",
  name: "Home Fiber 20 Mbps",
  description: "Paket internet hemat kecepatan tinggi yang dirancang khusus untuk keluarga modern. Nikmati streaming video 4K, gaming tanpa lag, dan bekerja dari rumah dengan koneksi stabil tanpa batas kuota (FUP).",
  price: 250000,
  discount: 15, // Diskon 15%
  discount_start: "2023-10-01 00:00:00",
  discount_end: "2023-12-31 23:59:59",
  created_at: "2023-05-10 09:00:00",
  updated_at: "2023-10-05 14:20:00",
  features: [
    "Unlimited Quota (Tanpa FUP)",
    "Best Effort up to 20 Mbps",
    "Prioritas Support via WhatsApp",
    "Free Instalasi & Registrasi",
    "Router WiFi 5 Gratis Sewa",
    "Gratis 1 Bulan jika Berlangganan 1 Tahun"
  ],
};

/* ============================================
   KOMPONEN: PackageDetail
   ============================================ */
export const PackageDetail = () => {
  const [showSidebar, setShowSidebar] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  
  // Simulasi data paket (di real app gunakan useParams dan useEffect untuk fetch)
  const data = dummyPackageDetail;

  /* ---------- Logic Harga & Promo ---------- */
  const pricingInfo = useMemo(() => {
    const now = new Date();
    const start = data.discount_start ? new Date(data.discount_start) : null;
    const end = data.discount_end ? new Date(data.discount_end) : null;
    
    // Cek apakah diskon sedang aktif berdasarkan rentang tanggal
    const isPromoActive = start && end 
      ? (now >= start && now <= end) 
      : (data.discount > 0); // Fallback jika tidak ada tanggal

    const finalPrice = isPromoActive 
      ? data.price - (data.price * (data.discount / 100)) 
      : data.price;

    return {
      isPromoActive,
      originalPrice: data.price,
      finalPrice,
      discountAmount: data.price - finalPrice,
      startDate: start,
      endDate: end
    };
  }, [data]);

  const handleDelete = () => {
    if (window.confirm("Apakah Anda yakin ingin menghapus paket ini? Tindakan ini tidak dapat dibatalkan.")) {
      setLoadingDelete(true);
      // Simulasi delete delay
      setTimeout(() => {
        setLoadingDelete(false);
        alert("Paket berhasil dihapus (Simulasi)");
        // navigate('/dashboard/packages'); 
      }, 1000);
    }
  };

  return (
    <>
      <Helmet>
        <title>Detail Paket - DANELA NET</title>
        <meta name="description" content={`Detail paket ${data.name}`} />
      </Helmet>

      <DashboardLayout
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
      >
        {/* ===== Custom Header dengan Breadcrumb ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex flex-col">
            {/* Breadcrumb */}
            <nav className="flex items-center text-xs text-slate-400 mb-1 font-medium">
              <Link to="/dashboard/packages" className="hover:text-cyan-400 transition-colors flex items-center gap-1">
                <FaArrowLeft className="text-[10px]" /> Paket
              </Link>
              <span className="mx-2 text-slate-600">/</span>
              <span className="text-slate-200">Detail</span>
            </nav>
            
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10 border border-purple-500/20">
                  <FaBoxOpen className="text-purple-400 text-lg" />
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-100">
                  {data.name}
                </h1>
                {pricingInfo.isPromoActive && (
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-pink-500/12 text-pink-400 border border-pink-500/20">
                    Promo Aktif
                  </span>
                )}
              </div>

              <div className="flex gap-2">
                 <button
                  onClick={handleDelete}
                  disabled={loadingDelete}
                  className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-all text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingDelete ? (
                     "Menghapus..."
                  ) : (
                    <>
                      <FaTrashCan className="text-xs" /> Hapus
                    </>
                  )}
                </button>
                <Link to="/dashboard/packages/edit">
                  <BtnDefault>
                    <span className="hidden lg:flex items-center gap-2">
                      <FaEdit className="text-xs" /> Edit Paket
                    </span>
                    <FaEdit className="lg:hidden" />
                  </BtnDefault>
                </Link>
              </div>
            </div>
          </div>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-6">
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* ===== KOLOM KIRI (Info Utama) ===== */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Card: Informasi Dasar */}
              <section
                className="rounded-2xl p-6"
                style={{
                  background: "rgba(15, 23, 42, 0.5)",
                  border: "1px solid rgba(51, 65, 85, 0.3)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <h3 className="text-lg font-bold text-gray-100 mb-4 flex items-center gap-2">
                  <FaInfoCircle className="text-cyan-500" />
                  Informasi Paket
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-4">
                  {/* ID Paket */}
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                      ID Paket
                    </label>
                    <div className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-slate-300 text-sm font-mono">
                      {data.id}
                    </div>
                  </div>

                  {/* Deskripsi */}
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                      Deskripsi
                    </label>
                    <div className="px-4 py-3 rounded-xl bg-slate-800/30 border border-slate-700/30 text-slate-300 text-sm leading-relaxed">
                      {data.description || "Tidak ada deskripsi."}
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-slate-700/30">
                     <div>
                        <span className="text-xs text-slate-500">Dibuat pada</span>
                        <p className="text-sm text-slate-400 mt-0.5">{formatDate(data.created_at)}</p>
                     </div>
                     <div>
                        <span className="text-xs text-slate-500">Terakhir diupdate</span>
                        <p className="text-sm text-slate-400 mt-0.5">{formatDate(data.updated_at)}</p>
                     </div>
                  </div>
                </div>
              </section>

              {/* Card: Daftar Fitur */}
              <section
                className="rounded-2xl p-6"
                style={{
                  background: "rgba(15, 23, 42, 0.5)",
                  border: "1px solid rgba(51, 65, 85, 0.3)",
                  backdropFilter: "blur(12px)",
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-100 flex items-center gap-2">
                    <FaListCheck className="text-emerald-400" />
                    Fitur Unggulan
                  </h3>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                    {data.features.length} Item
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.features.map((feat, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/30 border border-slate-700/30 hover:bg-slate-800/50 transition-colors group"
                    >
                      <div className="mt-0.5 flex-shrink-0">
                        <FaCheckCircle className="text-emerald-400 text-sm" />
                      </div>
                      <span className="text-sm text-slate-300 group-hover:text-slate-100 transition-colors">
                        {feat}
                      </span>
                    </div>
                  ))}
                </div>
                
                {data.features.length === 0 && (
                    <div className="text-center py-8 text-slate-500 text-sm">
                        Belum ada fitur ditambahkan untuk paket ini.
                    </div>
                )}
              </section>

            </div>

            {/* ===== KOLOM KANAN (Harga & Promo) ===== */}
            <div className="lg:col-span-1 space-y-6">
              
              {/* Card: Pricing Summary */}
              <section
                className="rounded-2xl p-6 sticky top-24"
                style={{
                  background: "linear-gradient(160deg, rgba(6, 182, 212, 0.08) 0%, rgba(15, 23, 42, 0.6) 100%)",
                  border: "1px solid rgba(6, 182, 212, 0.15)",
                  backdropFilter: "blur(12px)",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)",
                }}
              >
                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-6 flex items-center gap-2">
                  <FaTag />
                  Detail Harga
                </h3>

                <div className="space-y-6">
                  {/* Harga Dasar */}
                  <div className="flex justify-between items-end border-b border-slate-700/50 pb-4">
                    <span className="text-sm text-slate-400">Harga Normal</span>
                    <span className="text-lg font-mono font-medium text-slate-300">
                      {formatRupiah(data.price)}
                    </span>
                  </div>

                  {/* Diskon Section */}
                  <div className={`p-4 rounded-xl border transition-all ${pricingInfo.isPromoActive ? 'bg-pink-500/10 border-pink-500/20' : 'bg-slate-800/50 border-slate-700/50 opacity-60'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-bold uppercase flex items-center gap-1.5">
                        <FaPercent className="text-[10px]" />
                        Diskon
                      </span>
                      <span className={`text-lg font-bold ${pricingInfo.isPromoActive ? 'text-pink-400' : 'text-slate-500'}`}>
                        {data.discount > 0 ? `${data.discount}%` : '0%'}
                      </span>
                    </div>

                    {/* Tanggal Promo */}
                    {data.discount > 0 && (
                       <div className="space-y-1 mt-3">
                          <div className="flex items-center gap-2 text-[10px] text-slate-400">
                              <FaCalendarAlt />
                              <span>Mulai: {data.discount_start ? data.discount_start.split(' ')[0] : '-'}</span>
                          </div>
                          <div className="flex items-center gap-2 text-[10px] text-slate-400">
                              <FaCalendarAlt />
                              <span>Berakhir: {data.discount_end ? data.discount_end.split(' ')[0] : '-'}</span>
                          </div>
                          {!pricingInfo.isPromoActive && (
                              <div className="mt-2 text-[10px] text-orange-400 font-medium italic">
                                  * Promo tidak aktif saat ini
                              </div>
                          )}
                       </div>
                    )}
                  </div>

                  {/* Total Harga */}
                  <div className="pt-2">
                    <span className="text-xs text-slate-500 uppercase tracking-wider block mb-1">
                      Total Biaya Langganan
                    </span>
                    <div className="text-3xl font-bold text-cyan-400 font-display">
                      {formatRupiah(pricingInfo.finalPrice)}
                      <span className="text-sm font-normal text-slate-400">/bulan</span>
                    </div>
                    {pricingInfo.isPromoActive && (
                        <div className="text-xs text-emerald-400 mt-1 font-medium">
                            Hemat {formatRupiah(pricingInfo.discountAmount)} dari harga normal
                        </div>
                    )}
                  </div>
                </div>

                {/* Action Sticky Button Mobile */}
                <div className="mt-8 pt-6 border-t border-slate-700/30 lg:hidden">
                     <button
                      onClick={handleDelete}
                      className="w-full py-3 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-all text-sm font-semibold mb-3"
                    >
                      Hapus Paket
                    </button>
                </div>

              </section>
            </div>
          </div>
        </main>
      </DashboardLayout>
    </>
  );
};